import { useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router';
import { storage } from '@good-season/platform';
import { Button } from '@good-season/ui';

import { pendingEvents } from '../scorebook/db.js';
import { drainOnce } from '../scorebook/sync.js';
import type { WaitingUpdate } from './update-store.js';
import { getServerUpdateSnapshot, getUpdateSnapshot, subscribeToUpdates } from './update-store.js';
import { UPDATE_ARMED_KEY, isScorebookPath } from './whats-new.js';

/**
 * "A new version of Good Season is ready", with one action: Reload.
 *
 * A strip above the header, not a Dialog and not a Toast. A Dialog would cover
 * the screen, and below 640px it becomes a Sheet. A Toast cannot carry Reload
 * and would dismiss itself. The strip does not trap focus. It is hidden on
 * `/scorebook/*` so a live game is never asked to reload.
 *
 * Reload sends anything still in the offline queue first. If a play is still
 * only on this device afterwards, the page stays and the strip says so.
 * Anything that cannot be read from the queue also refuses the reload: a
 * reload that might drop a play is worse than staying on this version.
 */
export function UpdatePrompt() {
  const { pathname } = useLocation();
  const onScorebook = isScorebookPath(pathname);
  /*
   * Read again just before the reload: the person can walk into a game while
   * the queue is still being sent, and hiding the button does not stop work
   * a press already started.
   */
  const onScorebookNow = useRef(onScorebook);
  onScorebookNow.current = onScorebook;
  /*
   * One snapshot carrying both, because `useSyncExternalStore` compares with
   * `Object.is`. Reading the hold separately during render meant taking one
   * produced an identical snapshot, React correctly skipped the re-render, and
   * the button survived a hold that existed to remove it.
   */
  const { waiting, holdReason } = useSyncExternalStore(
    subscribeToUpdates,
    getUpdateSnapshot,
    getServerUpdateSnapshot,
  );

  /*
   * WHICH update is being applied, not merely that one is.
   *
   * `apply` is async and the reload arrives later, from `controlling`, after
   * the new worker activates. Holding the update object rather than a flag
   * means a newer waiting update is a different object, so a second version
   * offers Reload again instead of staying on "Reloading."
   */
  const [applyingFor, setApplyingFor] = useState<WaitingUpdate | null>(null);
  const applying = waiting !== null && applyingFor === waiting;
  const [queueBlocked, setQueueBlocked] = useState(false);

  const held = holdReason;

  return (
    /*
     * `role="status"` with `aria-live="polite"`, not `alert`. It is news, not an
     * emergency: polite waits for a screen reader to finish its sentence.
     *
     * The live region is mounted from the FIRST render and empty, rather than
     * appearing with its content already inside it. A region inserted into the
     * document already populated is commonly not announced at all.
     */
    <div className="updatePrompt__live" role="status" aria-live="polite">
      {waiting === null || onScorebook ? null : (
        <div className="updatePrompt">
          <div className="updatePrompt__text">
            <p className="updatePrompt__title">A new version of Good Season is ready.</p>
            <p className="updatePrompt__body">
              {held !== null
                ? held
                : queueBlocked
                  ? 'Saved plays are still on this device. Reload waits until they have been sent.'
                  : 'Reload updates the app.'}
            </p>
          </div>
          <div className="updatePrompt__actions">
            {applying ? (
              <p className="updatePrompt__applying">Reloading.</p>
            ) : held === null ? (
              <Button
                className="updatePrompt__reload"
                onClick={() => {
                  setApplyingFor(waiting);
                  setQueueBlocked(false);
                  void (async () => {
                    try {
                      await drainOnce();
                      const pending = await pendingEvents();
                      if (pending.length > 0) {
                        setApplyingFor((current) => (current === waiting ? null : current));
                        setQueueBlocked(true);
                        return;
                      }
                    } catch {
                      /*
                       * The queue could not be read or sent. Do not reload:
                       * a play might still be only on this device.
                       */
                      setApplyingFor((current) => (current === waiting ? null : current));
                      setQueueBlocked(true);
                      return;
                    }
                    if (onScorebookNow.current) {
                      setApplyingFor((current) => (current === waiting ? null : current));
                      return;
                    }
                    storage.writeValue(UPDATE_ARMED_KEY, '1');
                    try {
                      await waiting.apply();
                    } catch {
                      storage.writeValue(UPDATE_ARMED_KEY, '0');
                      /*
                       * Clear only if this is still the update being applied.
                       * A later version can already be in flight by the time
                       * this rejection arrives.
                       */
                      setApplyingFor((current) => (current === waiting ? null : current));
                    }
                  })();
                }}
              >
                Reload
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
